// src/utils/error/aiChatErrorMessage.js
//
// Maps a thrown error from sendMessage / startStream into a localized chat
// bubble message. Distinct from the sticky AiErrorBanner copy; this is what
// renders inside the assistant message when a request fails inline.
//
// Returning '' here is intentional for cases the banner already covers
// (license / cap / rate limit) — the bubble stays empty, banner does the
// surfacing, no duplicate UI.

const isEs = (locale) => String(locale || 'en').toLowerCase().startsWith('es');

const CHAT_ERROR_TABLE = {
  AI_LICENSE_BLOCKED:        { en: '', es: '' },
  AI_NOT_ENABLED:            { en: '', es: '' },
  AI_USAGE_CAP_REACHED:      { en: '', es: '' },
  RATE_LIMITED:              { en: '', es: '' },
  UNAUTHORIZED: {
    en: 'You were signed out. Sign back in to continue.',
    es: 'Sesión cerrada. Inicia sesión de nuevo para continuar.',
  },
  FORBIDDEN: {
    en: 'You don\'t have access to this. Ask your admin if you should.',
    es: 'No tienes acceso a esto. Consulta con tu administrador.',
  },
  VALIDATION_ERROR: {
    en: 'I couldn\'t understand that. Try rephrasing.',
    es: 'No pude entender eso. Intenta reformularlo.',
  },
  NETWORK_ERROR: {
    en: 'Couldn\'t reach the server. Check your connection and tap retry.',
    es: 'No pude conectar con el servidor. Revisa tu conexión y reintenta.',
  },
  ANTHROPIC_ERROR: {
    en: 'The AI service had a hiccup. Try again in a moment.',
    es: 'El servicio de IA tuvo un problema. Intenta de nuevo en un momento.',
  },
  TRANSCRIPTION_ERROR: {
    en: 'I couldn\'t hear you clearly. Try recording again.',
    es: 'No pude escucharte con claridad. Intenta grabar de nuevo.',
  },
  EMPTY_TRANSCRIPT: {
    en: 'I didn\'t catch that. Hold the mic and speak again.',
    es: 'No alcancé a escuchar. Mantén el micrófono y habla de nuevo.',
  },
};

const FALLBACK = {
  en: 'Something didn\'t go through. Try again.',
  es: 'Algo no se pudo enviar. Intenta de nuevo.',
};

export function getAiChatErrorMessage(errorOrCode, locale) {
  const code =
    typeof errorOrCode === 'string'
      ? errorOrCode
      : (errorOrCode && typeof errorOrCode === 'object' && typeof errorOrCode.code === 'string')
        ? errorOrCode.code
        : null;

  const lang = isEs(locale) ? 'es' : 'en';

  if (code && Object.prototype.hasOwnProperty.call(CHAT_ERROR_TABLE, code)) {
    return CHAT_ERROR_TABLE[code][lang] ?? '';
  }

  return FALLBACK[lang];
}
